import type { JSX } from 'react';
import { DataTableFilters } from '@/shared/components/DataTableFilters';
import { ResultCount } from '@/shared/components/ResultCount';
import { useTableFilters } from '@/shared/hooks/useTableFilters';
import { ConfirmDialog } from '../ConfirmDialog';
import {
  CandidatesEmptyState,
  CandidatesErrorState,
  CandidatesLoadingState,
} from './JobPositionCandidateStates';
import {
  JobPositionCandidateRows,
  type JobPositionCandidateHandlers,
} from './JobPositionCandidateRows';
import { useJobPositionCandidateActions } from '../../hooks/useJobPositionCandidateActions';
import {
  jobPositionCandidateTableColumns,
  JOB_POSITION_CANDIDATE_DEFAULT_SORT,
} from '../../config/jobPositionCandidateTableColumns';
import type { JobPositionCandidateWithDetails } from '../../types/jobPositionCandidate.types';

interface JobPositionCandidatesListProps {
  positionId: string;
  candidates: JobPositionCandidateWithDetails[];
  /** Whether the query ever resolved. Distinguishes a cached empty list from a failed first load. */
  hasData: boolean;
  isLoading?: boolean;
  isError?: boolean;
}

type CandidateActions = ReturnType<typeof useJobPositionCandidateActions>;

function UnlinkConfirmDialog({ actions }: { actions: CandidateActions }): JSX.Element {
  return (
    <ConfirmDialog
      open={actions.confirmUnlink.open}
      onOpenChange={(open) => actions.setConfirmUnlink({ ...actions.confirmUnlink, open })}
      title="Unlink Candidate"
      description={`Are you sure you want to unlink "${actions.confirmUnlink.candidateName}" from this position?`}
      onConfirm={actions.handleConfirmUnlink}
      confirmLabel="Unlink"
    />
  );
}

export function JobPositionCandidatesList({
  positionId,
  candidates,
  hasData,
  isLoading = false,
  isError = false,
}: JobPositionCandidatesListProps): JSX.Element {
  const actions = useJobPositionCandidateActions(positionId);
  const { filteredSorted, sort, toggleSort, ...toolbar } = useTableFilters(
    candidates,
    jobPositionCandidateTableColumns,
    JOB_POSITION_CANDIDATE_DEFAULT_SORT
  );

  const itemHandlers: JobPositionCandidateHandlers = {
    onStatusChange: actions.handleStatusChange,
    onUnlink: actions.handleUnlinkClick,
    isUpdating: actions.isBusy,
  };

  if (isLoading) return <CandidatesLoadingState />;
  // A failed refetch keeps whatever resolved before, so only report the failure when nothing ever did.
  if (isError && !hasData) return <CandidatesErrorState />;
  if (candidates.length === 0) return <CandidatesEmptyState />;

  return (
    <div className="space-y-4">
      <ResultCount count={filteredSorted.length} />
      <DataTableFilters
        columns={jobPositionCandidateTableColumns}
        filters={toolbar.filters}
        activeFilterCount={toolbar.activeFilterCount}
        onFilterChange={toolbar.setFilter}
        onClearFilters={toolbar.clearFilters}
        searchTerm={toolbar.searchTerm}
        onSearchChange={toolbar.setSearchTerm}
        title="Candidate filters"
      />

      <JobPositionCandidateRows
        candidates={filteredSorted}
        sort={sort}
        onToggleSort={toggleSort}
        itemHandlers={itemHandlers}
      />

      <UnlinkConfirmDialog actions={actions} />
    </div>
  );
}
